import { cloneElement } from 'react';
import { Box, Tooltip, useTheme } from '@mui/material';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { ModuleIcon } from './ModuleIcon';
import { ModuleIconKey } from './iconRegistry';

interface ISidebarItemProps {
  // Aceita uma chave do registro de ícones do menu, ou (para itens que não
  // vêm de routes/index.tsx, ex.: o seletor de tema) um ícone já pronto.
  icon: ModuleIconKey | React.ReactElement;
  label: string;
  active?: boolean;
  expanded: boolean;
  hasChevron?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  height?: number;
  iconSize?: string;
  railButton?: boolean;
}

export const SidebarItem: React.FC<ISidebarItemProps> = ({
  icon,
  label,
  active = false,
  expanded,
  hasChevron = false,
  onClick,
  height = 48,
  iconSize = '1.4rem',
  railButton = false,
}) => {
  const theme = useTheme();
  const sidebar = theme.torq.sidebar;
  const iconColor = active ? sidebar.iconActive : sidebar.iconDefault;

  const content = (
    <Box
      {...(railButton ? { 'data-rail-button': '' } : {})}
      onClick={onClick}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: expanded ? 1.5 : 0,
        justifyContent: expanded ? 'flex-start' : 'center',
        height,
        width: expanded ? '100%' : height,
        // Formato "gota": presa na borda esquerda (sem radius) e arredondada
        // do lado direito. Na variante de sidebar escura o fundo do item
        // ativo é transparente (marcado só pela barra de acento + cor do
        // ícone, ver `iconColor`/`::before` abaixo); na variante de sidebar
        // clara `activeGradient` continua sendo uma cor sólida, então o
        // formato de "gota" ainda importa ali.
        borderRadius: active ? '0 18px 18px 0' : `${theme.shape.borderRadius}px`,
        marginRight: active && expanded ? '12px' : 0,
        // Padding-left maior que o padrão pra abrir espaço pra barra de
        // acento mais larga (18px) sem colidir com o ícone.
        pl: expanded ? '20px' : 0,
        pr: expanded ? '8px' : 0,
        cursor: 'pointer',
        color: active ? sidebar.activeText : sidebar.textSecondary,
        background: active ? sidebar.activeGradient : 'transparent',
        fontWeight: active ? 600 : 500,
        transition: 'background-color 150ms ease, background 150ms ease, color 150ms ease',
        '&:hover': {
          background: active ? sidebar.activeGradient : sidebar.hover,
          color: sidebar.activeText,
        },
        '&:hover .MuiSvgIcon-root': {
          color: sidebar.iconActive,
        },
        // Barra de acento na borda esquerda do item ativo — mais larga que
        // um simples fio (spec do menu lateral), pra funcionar como o
        // indicador principal de seleção já que o fundo fica transparente.
        '&::before': active
          ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '15%',
            bottom: '15%',
            width: '4px',
            borderRadius: '0 4px 4px 0',
            backgroundColor: sidebar.activeAccentBar,
          }
          : undefined,
      }}
    >
      {typeof icon === 'string' ? (
        <ModuleIcon name={icon} sx={{ fontSize: iconSize, flexShrink: 0, color: iconColor }} />
      ) : (
        cloneElement(icon, { sx: { fontSize: iconSize, flexShrink: 0, color: iconColor, ...(icon.props?.sx || {}) } })
      )}

      {expanded && (
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            // Um pouco maior que o padrão (0.875rem) — texto do menu expandido
            // pequeno demais pra leitura confortável (pedido explícito do
            // usuário).
            fontSize: '0.9375rem',
          }}
        >
          {label}
        </Box>
      )}

      {expanded && hasChevron && (
        <ChevronRightRounded sx={{ fontSize: '1.1rem', opacity: 0.8, flexShrink: 0 }} />
      )}
    </Box>
  );

  if (!expanded) {
    return (
      <Tooltip title={label} placement="right">
        {content}
      </Tooltip>
    );
  }

  return content;
};
